import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Flex, Text, Spinner } from "@radix-ui/themes";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useAccessLabelApi } from "@/v2/entities/access-label/AccessLabel.api";
import { normalizeLabel } from "@/v2/entities/access-label/accessLabel.utils";

interface AccessLabelsEditorProps {
  articleId?: string;
  snippetId?: string;
  initialLabels?: string[];
  onSave?: () => void | Promise<void>;
  saveButtonPosition?: "bottom" | "right";
}

type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export function AccessLabelsEditor({
  articleId,
  snippetId,
  initialLabels = [],
  onSave,
  saveButtonPosition = "bottom",
}: AccessLabelsEditorProps) {
  const accessLabelApi = useAccessLabelApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<string[]>(
    initialLabels.map(normalizeLabel)
  );

  const { data: allLabels = [], isLoading: isLoadingLabels } = useQuery({
    queryKey: ["access-labels"],
    queryFn: () => accessLabelApi.getAccessLabels(),
  });

  const setLabelsMutation = useMutation({
    mutationFn: (labelNames: string[]) =>
      accessLabelApi.setLabels({
        labelNames,
        ...(articleId && { articleIds: [articleId] }),
        ...(snippetId && { snippetIds: [snippetId] }),
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["access-labels"] });
      if (snippetId) {
        queryClient.invalidateQueries({ queryKey: ["snippet", snippetId] });
        queryClient.invalidateQueries({ queryKey: ["snippets"] });
      }
      if (onSave) {
        await onSave();
      }
    },
  });

  const options: SelectOption[] = useMemo(
    () =>
      allLabels.map((label) => ({
        value: label.name,
        label: label.name,
      })),
    [allLabels]
  );

  useEffect(() => {
    const normalizedInitial = initialLabels.map(normalizeLabel);
    setSelectedLabels(normalizedInitial);
  }, [initialLabels]);

  const handleLabelChange = useCallback((newLabels: string[]) => {
    const normalizedLabels = newLabels.map(normalizeLabel);
    setSelectedLabels(normalizedLabels);
  }, []);

  const handleSave = useCallback(() => {
    setLabelsMutation.mutate(selectedLabels);
  }, [selectedLabels, setLabelsMutation]);

  const toggle = useCallback(
    (raw: string) => {
      const normalized = normalizeLabel(raw);
      const next = new Set(selectedLabels.map(String));
      if (next.has(normalized)) {
        next.delete(normalized);
      } else {
        next.add(normalized);
      }
      handleLabelChange(Array.from(next) as string[]);
    },
    [selectedLabels, handleLabelChange]
  );

  const clearAll = useCallback(() => {
    handleLabelChange([]);
  }, [handleLabelChange]);

  const canCreate =
    search.trim().length > 0 &&
    !options.some((o) => {
      const normalizedSearch = normalizeLabel(search.trim());
      return (
        normalizeLabel(o.label) === normalizedSearch ||
        normalizeLabel(String(o.value)) === normalizedSearch
      );
    }) &&
    !selectedLabels.some(
      (label) => normalizeLabel(label) === normalizeLabel(search.trim())
    );

  const handleCreate = useCallback(() => {
    const normalized = normalizeLabel(search.trim());
    if (!normalized) return;
    handleLabelChange([...selectedLabels, normalized]);
    setSearch("");
  }, [search, selectedLabels, handleLabelChange]);

  if (isLoadingLabels) {
    return (
      <Flex align="center" gap="2">
        <Spinner size="2" />
        <Text size="2" color="gray">
          Loading labels...
        </Text>
      </Flex>
    );
  }

  const popoverContent = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={saveButtonPosition === "right" ? "flex-1 justify-between px-3" : "w-full justify-between px-3"}
        >
          <div className="flex flex-wrap items-center gap-1 text-left min-w-0 flex-1">
            {selectedLabels.length === 0 ? (
              <span className="text-muted-foreground">
                Select or create labels
              </span>
            ) : (
              <>
                {selectedLabels.slice(0, 2).map((v) => {
                  const opt = options.find(
                    (o) =>
                      normalizeLabel(String(o.value)) === normalizeLabel(v)
                  );
                  return (
                    <Badge
                      key={v}
                      variant="secondary"
                      className="flex items-center gap-1 shrink-0"
                    >
                      <span className="truncate max-w-[120px]">
                        {opt?.label ?? v}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${opt?.label ?? v}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(v);
                        }}
                        className="ml-1 inline-flex shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
                {selectedLabels.length > 2 && (
                  <Badge variant="secondary" className="shrink-0">
                    +{selectedLabels.length - 2}
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter>
          <div className="p-2">
            <CommandInput
              placeholder="Search or create label…"
              value={search}
              onValueChange={setSearch}
            />
          </div>

          <CommandEmpty className="px-3 py-2 text-sm text-muted-foreground">
            {canCreate ? (
              <div className="flex items-center justify-between gap-2">
                <span>
                  No results. Create{" "}
                  <span className="font-medium">
                    &quot;{normalizeLabel(search.trim())}&quot;
                  </span>
                  ?
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCreate}
                >
                  Add
                </Button>
              </div>
            ) : (
              "No results"
            )}
          </CommandEmpty>

          <CommandList className="max-h-60 overflow-y-auto">
            <CommandGroup>
              {options.map((o) => {
                const normalizedValue = normalizeLabel(String(o.value));
                const selected = selectedLabels.some(
                  (label) => normalizeLabel(label) === normalizedValue
                );
                return (
                  <CommandItem
                    key={String(o.value)}
                    value={String(o.label)}
                    onSelect={() => !o.disabled && toggle(String(o.value))}
                    disabled={o.disabled}
                    className="cursor-pointer"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        selected ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span>{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>

          <Separator />
          <div className="flex items-center justify-between p-2">
            <span className="text-xs text-muted-foreground">
              {selectedLabels.length} selected
            </span>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={clearAll}
              disabled={selectedLabels.length === 0}
            >
              Clear
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );

  const saveButton = (
    <Button
      type="button"
      onClick={handleSave}
      disabled={setLabelsMutation.isLoading}
      className={saveButtonPosition === "right" ? "" : "w-full"}
    >
      {setLabelsMutation.isLoading ? "Saving..." : "Save Labels"}
    </Button>
  );

  if (saveButtonPosition === "right") {
    return (
      <Flex direction="column" gap="3">
        <Text size="2" weight="medium">
          Access Labels
        </Text>
        <Flex gap="2" align="end">
          {popoverContent}
          {saveButton}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="3">
      <Text size="2" weight="medium">
        Access Labels
      </Text>
      {popoverContent}
      {saveButton}
    </Flex>
  );
}
